import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { useTenantBranding } from '../../contexts/ThemeContext';
import {
  useUpdateBranding,
  useApplyThemePreset,
  THEME_PRESETS,
} from '../../hooks/queries/useTenantBranding';
import type { ThemePresetKey } from '../../hooks/queries/useTenantBranding';
import type { TablesUpdate } from '../../types/database.types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LogoUploader } from './LogoUploader';
import { ColorPickerField } from './ColorPickerField';
import { FontPickerField } from './FontPickerField';
import { BrandingPreviewPanel } from './BrandingPreviewPanel';

import { DateFormatSection } from './DateFormatSection';
const PRIMARY_PRESETS = ['#043429', '#1e3a5f', '#4a1d2e', '#2d2d2d', '#1a3a2e'];
const ACCENT_PRESETS = ['#718063', '#6b8e6b', '#8b7355', '#5f7a8a', '#7a6c5d'];
const GOLD_PRESETS = ['#c9a84c', '#b8860b', '#daa520', '#a0896a', '#d4af37'];

const BORDER_RADIUS_OPTIONS = [
  { value: '4px', label: 'Compact' },
  { value: '8px', label: 'Standard' },
  { value: '12px', label: 'Arrondi' },
] as const;

const SIDEBAR_STYLE_OPTIONS: Array<{
  value: 'light' | 'tinted' | 'dark';
  label: string;
  description: string;
}> = [
  { value: 'light', label: 'Claire', description: 'Fond blanc, accents de couleur' },
  { value: 'tinted', label: 'Teintée', description: 'Fond légèrement teinté' },
  { value: 'dark', label: 'Sombre', description: 'Fond primaire, texte blanc' },
];

const PRESET_LABELS: Record<ThemePresetKey, string> = {
  cime_classic: 'CIME Classic',
  midnight: 'Midnight',
  sunset: 'Sunset',
  forest: 'Forest',
  marine: 'Marine',
  bordeaux: 'Bordeaux',
};

interface BrandingFormValues {
  color_primary: string;
  color_accent: string;
  color_gold: string;
  font_heading: string;
  font_heading_url: string;
  font_body: string;
  font_body_url: string;
  border_radius: string;
  sidebar_style: 'light' | 'tinted' | 'dark';
  color_text_primary: string;
  color_text_muted: string;
  color_danger: string;
  color_success: string;
  color_warning: string;
}

function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

const COLOR_FIELD_NAMES: Array<keyof BrandingFormValues> = [
  'color_primary',
  'color_accent',
  'color_gold',
  'sidebar_style',
  'color_text_primary',
  'color_text_muted',
  'color_danger',
  'color_success',
  'color_warning',
];

export function BrandingTab() {
  const { branding, isLoading } = useTenantBranding();
  const updateBranding = useUpdateBranding();
  const applyPreset = useApplyThemePreset();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = useForm<BrandingFormValues>({
    defaultValues: {
      color_primary: branding.color_primary,
      color_accent: branding.color_accent,
      color_gold: branding.color_gold,
      font_heading: branding.font_heading,
      font_heading_url: branding.font_heading_url ?? '',
      font_body: branding.font_body,
      font_body_url: branding.font_body_url ?? '',
      border_radius: branding.border_radius,
      sidebar_style: (branding.sidebar_style as 'light' | 'tinted' | 'dark') ?? 'light',
      color_text_primary: branding.color_text_primary,
      color_text_muted: branding.color_text_muted,
      color_danger: branding.color_danger,
      color_success: branding.color_success,
      color_warning: branding.color_warning,
    },
  });

  // Sync form when branding reloads after save or preset apply
  useEffect(() => {
    reset({
      color_primary: branding.color_primary,
      color_accent: branding.color_accent,
      color_gold: branding.color_gold,
      font_heading: branding.font_heading,
      font_heading_url: branding.font_heading_url ?? '',
      font_body: branding.font_body,
      font_body_url: branding.font_body_url ?? '',
      border_radius: branding.border_radius,
      sidebar_style: (branding.sidebar_style as 'light' | 'tinted' | 'dark') ?? 'light',
      color_text_primary: branding.color_text_primary,
      color_text_muted: branding.color_text_muted,
      color_danger: branding.color_danger,
      color_success: branding.color_success,
      color_warning: branding.color_warning,
    });
  }, [branding, reset]);

  // Watch live values for preview
  const watched = watch();

  // Determine if any color/layout field is dirty (for theme_preset: 'custom')
  const hasColorDirtyField = COLOR_FIELD_NAMES.some(
    (name) => dirtyFields[name],
  );

  async function onSubmit(values: BrandingFormValues) {
    const payload: TablesUpdate<'tenant_branding'> = {};

    if (dirtyFields.color_primary && isValidHex(values.color_primary)) {
      payload.color_primary = values.color_primary;
    }
    if (dirtyFields.color_accent && isValidHex(values.color_accent)) {
      payload.color_accent = values.color_accent;
    }
    if (dirtyFields.color_gold && isValidHex(values.color_gold)) {
      payload.color_gold = values.color_gold;
    }
    if (dirtyFields.font_heading) payload.font_heading = values.font_heading;
    if (dirtyFields.font_heading_url) {
      payload.font_heading_url = values.font_heading_url || null;
    }
    if (dirtyFields.font_body) payload.font_body = values.font_body;
    if (dirtyFields.font_body_url) {
      payload.font_body_url = values.font_body_url || null;
    }
    if (dirtyFields.border_radius) payload.border_radius = values.border_radius;
    if (dirtyFields.sidebar_style) payload.sidebar_style = values.sidebar_style;
    if (dirtyFields.color_text_primary && isValidHex(values.color_text_primary)) {
      payload.color_text_primary = values.color_text_primary;
    }
    if (dirtyFields.color_text_muted && isValidHex(values.color_text_muted)) {
      payload.color_text_muted = values.color_text_muted;
    }
    if (dirtyFields.color_danger && isValidHex(values.color_danger)) {
      payload.color_danger = values.color_danger;
    }
    if (dirtyFields.color_success && isValidHex(values.color_success)) {
      payload.color_success = values.color_success;
    }
    if (dirtyFields.color_warning && isValidHex(values.color_warning)) {
      payload.color_warning = values.color_warning;
    }

    // If any colour/layout field changed manually, mark as custom
    if (hasColorDirtyField) {
      payload.theme_preset = 'custom';
    }

    if (Object.keys(payload).length === 0) return;

    try {
      await updateBranding.mutateAsync(payload);
      toast.success('Apparence mise à jour');
      reset(values); // mark form clean
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  }

  async function handlePresetClick(key: ThemePresetKey) {
    try {
      await applyPreset.mutateAsync(key);
      toast.success(`Thème "${PRESET_LABELS[key]}" appliqué`);
    } catch {
      toast.error('Erreur lors du changement de thème');
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <span
          className="w-6 h-6 rounded-full border-2 border-cime-primary border-t-transparent animate-spin"
          aria-label="Chargement en cours"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2
          className="text-xl font-semibold text-neutral-900"
        >
          Apparence du cabinet
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Personnalisez le logo, les couleurs et les polices. Les modifications sont prévisualisées
          en temps réel avant d'être enregistrées.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* ── Left: form ────────────────────────────────────────── */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex-1 min-w-0 space-y-6"
          noValidate
        >
          {/* ── Section A: Theme presets ──────────────────────── */}
          <Card
            header={
              <div>
                <p className="text-sm font-semibold text-neutral-800">Preset de thème</p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Appliquez un thème complet en un clic, puis personnalisez-le ensuite si besoin.
                </p>
              </div>
            }
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(Object.keys(THEME_PRESETS) as ThemePresetKey[]).map((key) => {
                const preset = THEME_PRESETS[key];
                const isActive = branding.theme_preset === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void handlePresetClick(key)}
                    disabled={applyPreset.isPending}
                    className={cn(
                      'relative flex flex-col gap-2 p-3 rounded-lg border-2 text-left transition-colors',
                      'hover:border-cime-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary',
                      isActive
                        ? 'border-cime-primary bg-cime-primary/5'
                        : 'border-neutral-200 bg-white',
                    )}
                  >
                    {/* Color swatches */}
                    <div className="flex gap-1.5">
                      <div
                        className="w-8 h-8 rounded-md border border-white shadow-sm"
                        style={{ backgroundColor: preset.color_primary }}
                        aria-hidden
                      />
                      <div className="flex flex-col gap-1">
                        <div
                          className="w-4 h-3.5 rounded-sm border border-white/80 shadow-sm"
                          style={{ backgroundColor: preset.color_accent }}
                          aria-hidden
                        />
                        <div
                          className="w-4 h-3.5 rounded-sm border border-white/80 shadow-sm"
                          style={{ backgroundColor: preset.color_gold }}
                          aria-hidden
                        />
                      </div>
                    </div>
                    {/* Label */}
                    <span
                      className="text-xs font-semibold text-neutral-800 truncate"
                      style={{ fontFamily: `'${preset.font_heading}', Georgia, serif` }}
                    >
                      {PRESET_LABELS[key]}
                    </span>
                    {/* Active badge */}
                    {isActive && (
                      <span className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-cime-primary text-white">
                        Actif
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* ── Section B: Sidebar style ──────────────────────── */}
          <Card
            header={
              <p className="text-sm font-semibold text-neutral-800">Layout</p>
            }
          >
            <div>
              <p className="text-xs text-neutral-600 mb-3 font-medium">Style de la sidebar</p>
              <Controller
                control={control}
                name="sidebar_style"
                render={({ field }) => (
                  <div
                    className="flex gap-2"
                    role="group"
                    aria-label="Choisir le style de la sidebar"
                  >
                    {SIDEBAR_STYLE_OPTIONS.map(({ value, label, description }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => field.onChange(value)}
                        className={cn(
                          'flex-1 flex flex-col gap-1 py-3 px-3 border-2 rounded-lg text-left transition-colors',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary',
                          field.value === value
                            ? 'bg-cime-primary border-cime-primary text-white'
                            : 'bg-white border-neutral-200 text-neutral-700 hover:border-cime-primary',
                        )}
                        aria-pressed={field.value === value}
                      >
                        <span className="text-sm font-medium">{label}</span>
                        <span
                          className={cn(
                            'text-xs',
                            field.value === value ? 'text-white/75' : 'text-neutral-500',
                          )}
                        >
                          {description}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              />
            </div>
          </Card>

          {/* ── Logo section ──────────────────────────────────── */}
          <Card>
            <LogoUploader currentLogoUrl={branding.logo_url} />
          </Card>

          {/* ── Colors section ────────────────────────────────── */}
          <Card
            header={
              <p className="text-sm font-semibold text-neutral-800">Couleurs</p>
            }
          >
            <div className="space-y-6">
              <Controller
                control={control}
                name="color_primary"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur primaire"
                    value={field.value}
                    presets={PRIMARY_PRESETS}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="color_accent"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur d'accent"
                    value={field.value}
                    presets={ACCENT_PRESETS}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="color_gold"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur or"
                    value={field.value}
                    presets={GOLD_PRESETS}
                    onChange={field.onChange}
                  />
                )}
              />

              {/* Divider */}
              <hr className="border-neutral-100" />

              {/* Text colors */}
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide -mb-2">
                Couleurs de texte
              </p>
              <Controller
                control={control}
                name="color_text_primary"
                render={({ field }) => (
                  <ColorPickerField
                    label="Texte principal"
                    value={field.value}
                    presets={['#1a1a1a', '#111827', '#0f172a', '#1e293b', '#292524']}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="color_text_muted"
                render={({ field }) => (
                  <ColorPickerField
                    label="Texte secondaire"
                    value={field.value}
                    presets={['#6b7280', '#78716c', '#94a3b8', '#9ca3af', '#71717a']}
                    onChange={field.onChange}
                  />
                )}
              />

              {/* Divider */}
              <hr className="border-neutral-100" />

              {/* Semantic colors */}
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide -mb-2">
                Couleurs sémantiques
              </p>
              <Controller
                control={control}
                name="color_danger"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur danger"
                    value={field.value}
                    presets={['#dc2626', '#b91c1c', '#ef4444', '#e11d48']}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="color_success"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur succès"
                    value={field.value}
                    presets={['#16a34a', '#15803d', '#22c55e', '#059669']}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="color_warning"
                render={({ field }) => (
                  <ColorPickerField
                    label="Couleur avertissement"
                    value={field.value}
                    presets={['#d97706', '#b45309', '#f59e0b', '#eab308']}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </Card>

          {/* ── Fonts section ─────────────────────────────────── */}
          <Card
            header={
              <p className="text-sm font-semibold text-neutral-800">Typographie</p>
            }
          >
            <div className="space-y-6">
              <Controller
                control={control}
                name="font_heading"
                render={({ field: headingField }) => (
                  <Controller
                    control={control}
                    name="font_heading_url"
                    render={({ field: urlField }) => (
                      <FontPickerField
                        label="Police des titres"
                        type="heading"
                        value={headingField.value}
                        customUrl={urlField.value}
                        onChange={headingField.onChange}
                        onCustomUrlChange={urlField.onChange}
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={control}
                name="font_body"
                render={({ field: bodyField }) => (
                  <Controller
                    control={control}
                    name="font_body_url"
                    render={({ field: urlField }) => (
                      <FontPickerField
                        label="Police du corps"
                        type="body"
                        value={bodyField.value}
                        customUrl={urlField.value}
                        onChange={bodyField.onChange}
                        onCustomUrlChange={urlField.onChange}
                      />
                    )}
                  />
                )}
              />
            </div>
          </Card>

          {/* ── Border radius section ─────────────────────────── */}
          <Card
            header={
              <p className="text-sm font-semibold text-neutral-800">Rayons des coins</p>
            }
          >
            <Controller
              control={control}
              name="border_radius"
              render={({ field }) => (
                <div
                  className="flex gap-2"
                  role="group"
                  aria-label="Choisir le rayon des coins"
                >
                  {BORDER_RADIUS_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => field.onChange(value)}
                      className={cn(
                        'flex-1 py-2 px-3 text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-cime-primary focus:ring-offset-1',
                        field.value === value
                          ? 'bg-cime-primary text-white border-cime-primary'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:border-cime-primary hover:text-cime-primary',
                      )}
                      style={{ borderRadius: value }}
                      aria-pressed={field.value === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            />
          </Card>

          {/* Save button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting || updateBranding.isPending}
              disabled={!isDirty}
            >
              Enregistrer l'apparence
            </Button>
          </div>
        </form>

        {/* ── Right: live preview ───────────────────────────────── */}
        <div className="w-full lg:w-72 shrink-0">
          <BrandingPreviewPanel
            primary={isValidHex(watched.color_primary) ? watched.color_primary : branding.color_primary}
            accent={isValidHex(watched.color_accent) ? watched.color_accent : branding.color_accent}
            gold={isValidHex(watched.color_gold) ? watched.color_gold : branding.color_gold}
            firmName={branding.firm_name}
            logoUrl={branding.logo_url}
            headingFont={watched.font_heading || branding.font_heading}
            bodyFont={watched.font_body || branding.font_body}
            borderRadius={watched.border_radius || branding.border_radius}
            sidebarStyle={watched.sidebar_style ?? 'light'}
          />
        </div>
      </div>

      <div className="mt-6">
        <DateFormatSection />
      </div>
    </div>
  );
}
